import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight } from "lucide-react";
import anchorLogo from "@/assets/anchor-logo.png";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface NavbarProps {
  isAuthenticated?: boolean;
  onLogout?: () => void;
}

export const Navbar = ({ isAuthenticated: isAuthenticatedProp, onLogout }: NavbarProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authState, setAuthState] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/" || location.pathname === "/pricing" || location.pathname === "/docs" || location.pathname.startsWith("/news");

  const isAuthenticated = isAuthenticatedProp ?? authState;

  useEffect(() => {
    if (isAuthenticatedProp !== undefined) return; // skip if parent manages auth
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setAuthState(!!session);
    };
    checkAuth();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setAuthState(!!session);
    });
    return () => subscription.unsubscribe();
  }, [isAuthenticatedProp]);

  const scrollToFeatures = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById("solution-section");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.getElementById("solution-section");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <nav className={`sticky top-0 z-50 border-b ${isLanding ? "bg-navy border-navy-light/20" : "bg-card border-border"}`}>
      <div className="container mx-auto px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" title="HostCaptain Home">
            <img src={anchorLogo} alt="HostCaptain logo" className="h-7 w-7" />
            <span className={`text-xl font-bold ${isLanding ? "text-white" : "text-foreground"}`}>
              HostCaptain
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {isLanding && (
              <>
                <Link 
                  to="/pricing" 
                  className="text-sm text-white/80 hover:text-white transition-colors"
                  title="View HostCaptain pricing plans"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Pricing
                </Link>
                <button 
                  onClick={scrollToFeatures}
                  className="text-sm text-white/80 hover:text-white transition-colors"
                  title="See HostCaptain features"
                >
                  Features
                </button>
                <Link 
                  to="/docs" 
                  className="text-sm text-white/80 hover:text-white transition-colors"
                  title="Read HostCaptain documentation"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Documentation
                </Link>
                <Link 
                  to="/news" 
                  className="text-sm text-white/80 hover:text-white transition-colors"
                  title="Latest Turo platform news and updates"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Turo News
                </Link>
              </>
            )}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/app/dashboard" title="Go to your dashboard">
                  <Button variant="ghost" className={isLanding ? "border border-white/20 text-white hover:text-white hover:bg-white/10" : ""}>
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  onClick={onLogout ?? (async () => { await supabase.auth.signOut(); })}
                  className={isLanding ? "text-white/80 hover:text-white hover:bg-white/10" : ""}
                >
                  Log out
                </Button>
              </>
            ) : (
              <>
                <Link to="/auth" title="Log in to HostCaptain">
                  <Button variant="ghost" className={isLanding ? "text-white/80 hover:text-white hover:bg-white/10" : ""}>
                    Log in
                  </Button>
                </Link>
                <Link to="/#get-started" title="Get started with HostCaptain" onClick={() => {
                  if (location.pathname === "/") {
                    const element = document.getElementById("hero-email-form");
                    if (element) {
                      element.scrollIntoView({ behavior: "smooth" });
                      const input = element.querySelector("input");
                      if (input) input.focus();
                    }
                  }
                }}>
                  <Button className="bg-gradient-primary pr-2.5">
                    Get Started<ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className={`h-6 w-6 ${isLanding ? "text-white" : "text-foreground"}`} />
            ) : (
              <Menu className={`h-6 w-6 ${isLanding ? "text-white" : "text-foreground"}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden py-4 border-t ${isLanding ? "border-white/10" : "border-border"}`}>
            <div className="flex flex-col gap-4">
              {isLanding && (
                <>
                  <Link 
                    to="/pricing" 
                    className={`text-sm ${isLanding ? "text-white/80" : "text-foreground"}`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    Pricing
                  </Link>
                  <button 
                    onClick={() => {
                      setMobileMenuOpen(false);
                      scrollToFeatures();
                    }}
                    className={`text-sm text-left ${isLanding ? "text-white/80" : "text-foreground"}`}
                  >
                    Features
                  </button>
                  <Link 
                    to="/docs" 
                    className={`text-sm ${isLanding ? "text-white/80" : "text-foreground"}`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    Documentation
                  </Link>
                  <Link 
                    to="/news" 
                    className={`text-sm ${isLanding ? "text-white/80" : "text-foreground"}`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    Turo News
                  </Link>
                </>
              )}
              {isAuthenticated ? (
                <>
                  <Link to="/app/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Dashboard</Button>
                  </Link>
                  <Button variant="ghost" onClick={onLogout ?? (async () => { await supabase.auth.signOut(); })} className="w-full text-white/80 hover:text-white hover:bg-white/10">
                    Log out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full text-white/80 hover:text-white hover:bg-white/10">Log in</Button>
                  </Link>
                  <Link to="/" onClick={() => {
                    setMobileMenuOpen(false);
                    setTimeout(() => {
                      const element = document.getElementById("hero-email-form");
                      if (element) {
                        element.scrollIntoView({ behavior: "smooth" });
                        const input = element.querySelector("input");
                        if (input) input.focus();
                      }
                    }, 100);
                  }}>
                    <Button className="w-full bg-gradient-primary pr-2.5">
                      Get Started<ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
